/**
 * TanStack Query client factory. One client per app instance; the @authe/*
 * feature hooks all read it from the same `QueryClientProvider`.
 */
import { QueryClient } from "@tanstack/react-query";
import { toErrorResponse, getCodeSpec } from "@authe/fe-commons/errors";

/** Defer retryability to the catalogued fe-commons error model. */
function isRetryable(error: unknown): boolean {
  const res = toErrorResponse(error);
  const code = res.error.error_code;
  if (code) {
    const spec = getCodeSpec(code);
    if (spec) return spec.retryable;
  }
  return res.statusCode >= 500 || res.statusCode === 429;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // DynamoDB-backed reads are cheap to cache; the ApiClient already does a
        // short in-memory GET cache + region failover underneath.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Non-retryable 5xx (CFG/INT/DATA) fail fast; transient ones get 1 retry.
        retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
      },
      mutations: {
        retry: false,
      },
    },
  });
}
